import i18n from "i18next";
import { SignInRequired } from "../../../auth/signIn";
import React from "react";
import {Button, Card, Col, Empty, Form, Input, Popconfirm, Row, Select, Space, Spin, Table, Tag, Typography} from "antd";
import { StatTile } from "@dowty/ui";
import type {EventCategoryStatistic, EventDrinkStatistic, EventTopConsumer, EventUserStatistics} from "../../../services/drinkService";
import type {EventStatsData} from "../useEventStats";
import {formatAlcohol, formatNumber, formatVolume} from "./utils";
import EventStatsHeader from "./EventStatsHeader";
import OngoingEventsCard from "./OngoingEventsCard";
import FeaturedEntryCard from "./FeaturedEntryCard";
import {EventVisibility} from "../../../generated/drinks_pb";
import dayjs from "dayjs";
import ParticipantBreakdown from "./ParticipantBreakdown";
import EventTimelineCard from "./EventTimelineCard";
const {Title, Text}=Typography;
type BreakdownEnv = Pick<React.ComponentProps<typeof ParticipantBreakdown>, "scroll" | "t">;

// Built outside the component so the row renderer is not a component defined inside a component.
const renderBreakdown = ({scroll, t}: BreakdownEnv) => (record: EventUserStatistics) => (
    <ParticipantBreakdown record={record} scroll={scroll} t={t} />
);

const EventStatsDashboard: React.FC<{data: EventStatsData}> = ({data}) => {
    const {isAuthenticated, t, events, selectedEventId, stats, loadingEvents, loadingStats, error,
        selectedDrinkId, visibleUserIds, allParticipantIds, ongoingLeaderboards, loadingOngoing,
        accessForm, updatingAccess, deletingSelectedEvent, isMobile, mobileTableScroll, visibilityOptions,
        visibilityOptionMap, selectedEvent, handleEventSelect, loadEvents, loadOngoing, setError, setSelectedDrinkId, setVisibleUserIds,
        handleSelectedEventAccessUpdate, handleDeleteSelectedEvent, visibleDrinks, visibleCategories,
        filteredUsers, filteredTotals, topAlcoholUser, timelineSeries, topBeerEntry,
        topShotsEntry, selectedDrinkLeaderboard, drinkOptions, participantOptions, userColumns, drinkColumns,
        categoryColumns, canManageSelectedEvent, requiresAccessPassword}=data;
    if (!isAuthenticated) {
        return (
            <SignInRequired title={t("eventStats.authRequired.title")} description={t("eventStats.authRequired.description")} />
        );
    }


    return (
        <div className="analytics-page event-stats-page">
            <EventStatsHeader
                events={events}
                selectedEventId={selectedEventId}
                loadingEvents={loadingEvents}
                onEventSelect={handleEventSelect}
                error={error}
                onDismissError={() => setError(null)}
                onRetry={() => { void Promise.all([loadEvents(), loadOngoing()]); }}
            />
            <OngoingEventsCard loading={loadingOngoing} leaderboards={ongoingLeaderboards} />

            {canManageSelectedEvent && selectedEvent && (
                <Card title={t("eventStats.visibilityCard.title")}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <Space align="center" size="large" wrap>
                            <Space direction="vertical" size={0}>
                                <Text type="secondary">{t("eventStats.visibilityCard.currentLabel")}</Text>
                                <Tag
                                    color={
                                        visibilityOptionMap.get(selectedEvent.visibility)?.color ?? "default"
                                    }
                                >
                                    {visibilityOptionMap.get(selectedEvent.visibility)?.label ??
                                        t("eventStats.visibilityOptions.unknown")}
                                    {selectedEvent.visibility === EventVisibility.EVENT_VISIBILITY_PASSWORD
                                        ? t("eventStats.visibilityOptions.passwordSuffix")
                                        : ""}
                                </Tag>
                            </Space>
                            <Text type="secondary">{t("eventStats.visibilityCard.description")}</Text>
                        </Space>
                        <Form layout="vertical" form={accessForm} onFinish={handleSelectedEventAccessUpdate}>
                            <Form.Item
                                label={t("eventStats.visibilityCard.form.visibilityLabel")}
                                name="visibility"
                                rules={[{ required: true, message: t("eventStats.visibilityCard.form.visibilityRequired") }]}
                            >
                                <Select
                                    options={visibilityOptions.map((option) => ({
                                        value: option.value,
                                        label: option.label,
                                    }))}
                                />
                            </Form.Item>
                            {requiresAccessPassword && (
                                <Form.Item
                                    label={t("eventStats.visibilityCard.form.passwordLabel")}
                                    name="password"
                                    rules={[{ required: true, message: t("eventStats.visibilityCard.form.passwordRequired") }]}
                                >
                                    <Input.Password
                                        placeholder={t("eventStats.visibilityCard.form.passwordPlaceholder")}
                                        maxLength={64}
                                        autoComplete="new-password"
                                    />
                                </Form.Item>
                            )}
                            <Form.Item>
                                <Button type="primary" htmlType="submit" loading={updatingAccess}>
                                    {t("eventStats.visibilityCard.form.submit")}
                                </Button>
                            </Form.Item>
                        </Form>
                        <Popconfirm
                            title={t("eventStats.visibilityCard.deleteConfirmTitle")}
                            description={t("eventStats.visibilityCard.deleteConfirmDescription")}
                            okText={t("eventStats.visibilityCard.deleteConfirmOk")}
                            okButtonProps={{ danger: true }}
                            onConfirm={handleDeleteSelectedEvent}
                        >
                            <Button danger loading={deletingSelectedEvent}>
                                {t("eventStats.visibilityCard.deleteButton")}
                            </Button>
                        </Popconfirm>
                    </Space>
                </Card>
            )}

            <Spin spinning={loadingStats} tip={t("common.loading")}>
                {!stats && !loadingStats && (
                    <Card>
                        <Empty description={t("eventStats.empty.selectPrompt")} />
                    </Card>
                )}
                {stats && (
                    <Space direction="vertical" size="large" style={{ width: "100%" }}>
                        <section className="event-stats-overview" aria-labelledby="event-stats-overview-label event-stats-overview-title">
                            <div className="event-stats-overview-title">
                                <Text id="event-stats-overview-label" type="secondary">{t("eventStats.summary.label")}</Text>
                                <Title id="event-stats-overview-title" level={2}>{stats.eventName}</Title>
                                <Text type="secondary">
                                    {selectedEvent?.start
                                        ? t("eventStats.summary.started", { time: dayjs(selectedEvent.start).toDate().toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" }) })
                                        : t("eventStats.summary.startUnknown")}
                                    {selectedEvent?.end ? t("eventStats.summary.ended", { time: dayjs(selectedEvent.end).toDate().toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" }) }) : ""}
                                </Text>
                            </div>
                            <Row gutter={[12, 12]} className="event-stats-summary">
                                <Col xs={24} sm={12} xl={6}><StatTile label={t("eventStats.summary.stats.visibleParticipants")} value={formatNumber(filteredUsers.length)} unit={stats.participantCount !== filteredUsers.length ? `/ ${formatNumber(stats.participantCount)}` : undefined} /></Col>
                                <Col xs={24} sm={12} xl={6}><StatTile label={t("eventStats.summary.stats.totalEntries")} value={formatNumber(Math.round(filteredTotals.totalEntries))} /></Col>
                                <Col xs={24} sm={12} xl={6}><StatTile label={t("eventStats.summary.stats.pureAlcohol")} value={formatAlcohol(filteredTotals.totalAlcohol)} /></Col>
                                <Col xs={24} sm={12} xl={6}><StatTile label={t("eventStats.summary.stats.totalLiquid")} value={formatVolume(filteredTotals.totalVolume)} /></Col>
                            </Row>
                        </section>

                        <Card title={t("eventStats.filters.title")} className="event-stats-filters">
                            <div className="event-stats-filter-row">
                                    <div className="event-stats-filter-control">
                                        <Text type="secondary"><label htmlFor="event-stats-drink-filter">{t("eventStats.filters.drinkLabel")}</label></Text>
                                        <Select
                                            id="event-stats-drink-filter"
                                            allowClear
                                            placeholder={t("eventStats.filters.drinkPlaceholder")}
                                            style={{ width: "100%" }}
                                            size={isMobile ? "middle" : "large"}
                                            value={selectedDrinkId ?? undefined}
                                            options={drinkOptions}
                                            onChange={(value) =>
                                                setSelectedDrinkId(value === undefined || value === null ? null : Number(value))
                                            }
                                        />
                                    </div>

                                    <div className="event-stats-filter-control event-stats-participants-filter">
                                        <Text type="secondary"><label htmlFor="event-stats-participants-filter">{t("eventStats.filters.participantsLabel")}</label></Text>
                                        <Select
                                            id="event-stats-participants-filter"
                                            mode="multiple"
                                            style={{ minWidth: 240 }}
                                            placeholder={t("eventStats.filters.participantsPlaceholder")}
                                            value={visibleUserIds}
                                            options={participantOptions}
                                            onChange={(values) =>
                                                setVisibleUserIds(
                                                    (values as Array<number | string>).map(Number)
                                                )
                                            }
                                            maxTagCount="responsive"
                                        />
                                        <Space size="small">
                                            <Button type="link" onClick={() => setVisibleUserIds(allParticipantIds)}>
                                                {t("eventStats.filters.showAll")}
                                            </Button>
                                            <Button type="link" onClick={() => setVisibleUserIds([])}>
                                                {t("eventStats.filters.hideAll")}
                                            </Button>
                                        </Space>
                                    </div>
                            </div>
                        </Card>

                        {topAlcoholUser.length > 0 && (
                            <Card title={t("eventStats.cards.topAlcoholTitle")}>
                                <Space direction="vertical" size={8}>
                                    {topAlcoholUser.map((user) => (
                                        <Space
                                            key={user.userId}
                                            direction="vertical"
                                            size={2}
                                            className="event-stats-ranked-entry"
                                        >
                                            <Text strong>{user.username}</Text>
                                            <Text type="secondary">
                                                {t("eventStats.cards.topAlcoholEntry", {
                                                    alcohol: formatAlcohol(user.totalAlcoholGrams),
                                                    volume: formatVolume(user.totalVolumeMl),
                                                    entries: user.activityCount,
                                                })}
                                            </Text>
                                        </Space>
                                    ))}
                                </Space>
                            </Card>
                        )}


                        {selectedDrinkLeaderboard && (
                            <Card
                                title={t("eventStats.cards.selectedDrinkTitle", {
                                    drink: selectedDrinkLeaderboard.drink.drinkName,
                                })}
                            >
                                <section className="event-stats-table" tabIndex={0 /* NOSONAR - required for keyboard scrolling */} aria-label={t("eventStats.cards.selectedDrinkTitle", {
                                    drink: selectedDrinkLeaderboard.drink.drinkName,
                                })}>
                                    <Table<EventTopConsumer>
                                        scroll={mobileTableScroll}
                                        size={isMobile ? "small" : "middle"}
                                        rowKey={(record) => record.userId}
                                        dataSource={selectedDrinkLeaderboard.participants}
                                        pagination={false}
                                        columns={[
                                            {
                                                title: t("eventStats.tables.selectedDrinkColumns.participant"),
                                                dataIndex: "username",
                                                key: "username",
                                                render: (value: string) => <Text strong>{value}</Text>,
                                            },
                                            {
                                                title: t("eventStats.tables.selectedDrinkColumns.entries"),
                                                dataIndex: "activityCount",
                                                render: (value: number) => formatNumber(value),
                                                key: "activityCount",
                                                align: "right",
                                            },
                                            {
                                                title: t("eventStats.tables.selectedDrinkColumns.liquid"),
                                                dataIndex: "totalVolumeMl",
                                                key: "drinkVolume",
                                                align: "right",
                                                render: (value: number) => formatVolume(value),
                                            },
                                            {
                                                title: t("eventStats.tables.selectedDrinkColumns.alcohol"),
                                                dataIndex: "totalAlcoholGrams",
                                                key: "drinkAlcohol",
                                                align: "right",
                                                render: (value: number) => formatAlcohol(value),
                                            },
                                        ]}
                                    />
                                </section>
                            </Card>
                        )}

                        <Card title={t("eventStats.cards.participantTitle")}>
                            <section className="event-stats-table" tabIndex={0 /* NOSONAR - required for keyboard scrolling */} aria-label={t("eventStats.cards.participantTitle")}>
                                <Table<EventUserStatistics>
                                    scroll={mobileTableScroll}
                                    rowKey={(record) => record.userId}
                                    columns={userColumns}
                                    dataSource={filteredUsers}
                                    pagination={false}
                                    size={isMobile ? "small" : undefined}
                                    locale={{ emptyText: t("eventStats.tables.participantsEmpty") }}
                                    expandable={{
                                        columnTitle: <span className="ui-sr-only">{t("eventStats.tables.expandColumn")}</span>,
                                        expandedRowRender: renderBreakdown({scroll: mobileTableScroll, t}),
                                    }}
                                />
                            </section>
                        </Card>





                        <EventTimelineCard
                            titleKey="eventStats.cards.timelineLiquidTitle"
                            emptyKey="eventStats.cards.timelineLiquidEmpty"
                            series={timelineSeries.volume}
                        />
                        <EventTimelineCard
                            titleKey="eventStats.cards.timelineAlcoholTitle"
                            emptyKey="eventStats.cards.timelineAlcoholEmpty"
                            series={timelineSeries.alcohol}
                            alcohol
                        />
                        <Row gutter={[16, 16]}>
                            <Col xs={24} lg={12}>
                                <FeaturedEntryCard
                                    entry={topBeerEntry}
                                    titleKey="eventStats.cards.topBeerTitle"
                                    fallbackKey="eventStats.cards.beerFallback"
                                    emptyKey="eventStats.cards.noBeerData"
                                    keyPrefix="beer"
                                />
                            </Col>
                            <Col xs={24} lg={12}>
                                <FeaturedEntryCard
                                    entry={topShotsEntry}
                                    titleKey="eventStats.cards.topShotsTitle"
                                    fallbackKey="eventStats.cards.shotsFallback"
                                    emptyKey="eventStats.cards.noShotsData"
                                    keyPrefix="shots"
                                />
                            </Col>
                        </Row>
                        <Row gutter={[16, 16]}>
                            <Col xs={24}>
                                <Card title={t("eventStats.cards.topDrinksTitle")}>
                                    <section className="event-stats-table" tabIndex={0 /* NOSONAR - required for keyboard scrolling */} aria-label={t("eventStats.cards.topDrinksTitle")}>
                                        <Table<EventDrinkStatistic>
                                            scroll={{ x: "max-content" }}
                                            rowKey={(record) => record.drinkId}
                                            columns={drinkColumns}
                                            dataSource={visibleDrinks}
                                            pagination={false}
                                            locale={{ emptyText: t("eventStats.tables.drinkEmpty") }}
                                        />
                                    </section>
                                </Card>
                            </Col>
                            <Col xs={24}>
                                <Card title={t("eventStats.cards.topCategoriesTitle")}>
                                    <section className="event-stats-table" tabIndex={0 /* NOSONAR - required for keyboard scrolling */} aria-label={t("eventStats.cards.topCategoriesTitle")}>
                                        <Table<EventCategoryStatistic>
                                            scroll={{ x: "max-content" }}
                                            rowKey={(record) => record.category}
                                            columns={categoryColumns}
                                            dataSource={visibleCategories}
                                            pagination={false}
                                            locale={{ emptyText: t("eventStats.tables.categoryEmpty") }}
                                        />
                                    </section>
                                </Card>
                            </Col>
                        </Row>
                    </Space>
                )}
            </Spin>
        </div>
    );
};

export default EventStatsDashboard;
